"use client";

/**
 * P9-05 · Browser third-party tags (GA4 gtag + Meta Pixel), loaded via
 * `next/script` only for the providers a brand has configured. GA4 Consent Mode
 * defaults every signal to `denied`; `ConsentBridge` (P11-10) flips them to
 * `granted` from the stored choice — on every load, not just at opt-in. A brand with nothing configured renders nothing and
 * loads zero external scripts. Secrets never appear here — only the public
 * measurement / pixel IDs.
 */
import Script from "next/script";

import { ConsentBridge } from "./consent-bridge";

import type { PublicAnalyticsConfig } from "~/server/api/schemas/brand-settings";

export function ThirdPartyAnalytics({ config }: { config: PublicAnalyticsConfig }) {
  const ga4 = config.ga4.enabled && config.ga4.measurementId ? config.ga4.measurementId : null;
  const pixel = config.meta.enabled && config.meta.pixelId ? config.meta.pixelId : null;

  if (!ga4 && !pixel) return null;

  return (
    <>
      {/*
        P11-10 · Replays the stored consent into gtag/fbq. Inside this component
        so it only exists when a tag is actually configured.
      */}
      <ConsentBridge />
      {ga4 ? (
        <>
          <Script
            id="ga4-src"
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${ga4}`}
          />
          <Script id="ga4-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              window.gtag = gtag;
              // Consent Mode v2 — denied until ConsentBridge applies the
              // visitor's stored choice (P11-10). Must stay BEFORE config.
              gtag('consent', 'default', {
                ad_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied',
                analytics_storage: 'denied',
              });
              gtag('js', new Date());
              gtag('config', '${ga4}');
            `}
          </Script>
        </>
      ) : null}

      {pixel ? (
        <Script id="meta-pixel" strategy="afterInteractive">
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window,document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${pixel}');
            fbq('track', 'PageView');
          `}
        </Script>
      ) : null}
    </>
  );
}
